import { create } from 'zustand';
import * as gitService from '@/services/git.service';
import type { GitStatus, GitLogEntry } from '@/services/git.service';

/* ================================================================== */
/*  State                                                             */
/* ================================================================== */

interface GitState {
  // Status
  branch: string;
  branches: string[];
  ahead: number;
  behind: number;
  staged: GitStatus['staged'];
  unstaged: GitStatus['unstaged'];
  untracked: string[];

  // Log
  log: GitLogEntry[];

  // UI state
  isLoading: boolean;
  commitMessage: string;
  error: string | null;

  // Actions
  fetchStatus: (workspaceId: string) => Promise<void>;
  fetchLog: (workspaceId: string) => Promise<void>;
  fetchBranches: (workspaceId: string) => Promise<void>;
  stageFiles: (workspaceId: string, paths: string[]) => Promise<void>;
  stageAll: (workspaceId: string) => Promise<void>;
  unstageFiles: (workspaceId: string, paths: string[]) => Promise<void>;
  commit: (workspaceId: string) => Promise<void>;
  checkout: (workspaceId: string, branch: string) => Promise<void>;
  createBranch: (workspaceId: string, name: string) => Promise<void>;
  setCommitMessage: (msg: string) => void;
  clearError: () => void;
}

/* ================================================================== */
/*  Store                                                             */
/* ================================================================== */

export const useGitStore = create<GitState>((set, get) => ({
  branch: 'main',
  branches: [],
  ahead: 0,
  behind: 0,
  staged: [],
  unstaged: [],
  untracked: [],
  log: [],
  isLoading: false,
  commitMessage: '',
  error: null,

  fetchStatus: async (workspaceId) => {
    set({ isLoading: true, error: null });
    try {
      const status = await gitService.getStatus(workspaceId);
      set({
        branch: status.branch,
        ahead: status.ahead,
        behind: status.behind,
        staged: status.staged,
        unstaged: status.unstaged,
        untracked: status.untracked,
        isLoading: false,
      });
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch git status',
      });
    }
  },

  fetchLog: async (workspaceId) => {
    try {
      const log = await gitService.getLog(workspaceId);
      set({ log });
    } catch {
      // Silently fail for log
    }
  },

  fetchBranches: async (workspaceId) => {
    try {
      const { branches, current } = await gitService.getBranches(workspaceId);
      set({ branches, branch: current });
    } catch {
      // Silently fail
    }
  },

  stageFiles: async (workspaceId, paths) => {
    try {
      await gitService.stageFiles(workspaceId, paths);
      await get().fetchStatus(workspaceId);
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to stage files' });
    }
  },

  stageAll: async (workspaceId) => {
    const state = get();
    const allPaths = [
      ...state.unstaged.map((f) => f.path),
      ...state.untracked,
    ];
    if (allPaths.length > 0) {
      await get().stageFiles(workspaceId, allPaths);
    }
  },

  unstageFiles: async (workspaceId, paths) => {
    try {
      await gitService.unstageFiles(workspaceId, paths);
      await get().fetchStatus(workspaceId);
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to unstage files' });
    }
  },

  commit: async (workspaceId) => {
    const { commitMessage } = get();
    if (!commitMessage.trim()) {
      set({ error: 'Commit message is required' });
      return;
    }
    set({ isLoading: true, error: null });
    try {
      await gitService.commit(workspaceId, commitMessage.trim());
      set({ commitMessage: '', isLoading: false });
      // Refresh status and log
      await Promise.all([
        get().fetchStatus(workspaceId),
        get().fetchLog(workspaceId),
      ]);
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Commit failed',
      });
    }
  },

  checkout: async (workspaceId, branch) => {
    set({ isLoading: true, error: null });
    try {
      await gitService.checkout(workspaceId, branch);
      set({ branch, isLoading: false });
      await get().fetchStatus(workspaceId);
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Checkout failed',
      });
    }
  },

  createBranch: async (workspaceId, name) => {
    set({ isLoading: true, error: null });
    try {
      await gitService.createBranch(workspaceId, name);
      set({ isLoading: false });
      await get().fetchBranches(workspaceId);
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to create branch',
      });
    }
  },

  setCommitMessage: (msg) => set({ commitMessage: msg }),
  clearError: () => set({ error: null }),
}));
